import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import { Badge } from '@/components/ui/badge';

import { useOidcCredentialStatus } from '../api';

/**
 * Live connectivity badge for one OIDC credential, queried on mount (PTV2-1383). Green "Connected" when
 * the issuer's discovery document + JWKS resolve; red "Error" with the diagnostic code on hover
 * otherwise. Mirrors the email-server status badge. The same credential id is reused across the OIDC
 * applications table and the auth-providers table, so React Query dedupes the probe.
 */
export function OidcCredentialStatusBadge({
  credentialId,
}: {
  readonly credentialId: string;
}): ReactElement {
  const { t } = useTranslation();
  const status = useOidcCredentialStatus(credentialId);
  const sp = 'routes.admin.oidcCredentials.status';

  if (status.isPending) {
    return <Badge variant="outline">{t(`${sp}.checking`)}</Badge>;
  }

  if (status.isError) {
    return (
      <Badge variant="destructive" title={t(`${sp}.failedTitle`, { code: getErrorCodeForUi(status.error) })}>
        {t(`${sp}.failed`)}
      </Badge>
    );
  }

  const data = status.data;
  if (data.ok) {
    return (
      <Badge variant="secondary" title={t(`${sp}.okTitle`, { issuer: data.issuer ?? '' })}>
        {t(`${sp}.ok`)}
      </Badge>
    );
  }

  return (
    <Badge
      variant="destructive"
      title={t(`${sp}.failedDetail`, {
        code: data.error_code ?? 'unknown',
        detail: data.detail ?? '',
      })}
    >
      {t(`${sp}.failed`)}
    </Badge>
  );
}
